import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FixedSizeList } from 'react-window';
import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { DoesFilterPassParams } from 'ag-grid-community';
import { useGridFilterDisplay } from 'ag-grid-react';
import { Box, TextField } from '@mui/material';

const Row = ({ index, style, data }: { index: number, style: React.CSSProperties, data: { options: string[], selectedOptions: string[], toggleStatus: (option: string) => void, enableSearch: boolean, refInput: React.RefObject<HTMLInputElement> } }) => {
    const { options, selectedOptions, toggleStatus, enableSearch, refInput } = data;
    const option = options[index];

    return (
        <div style={style}>
            <label
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: index === options.length - 1 ? '0' : '4px',
                    fontSize: '12px',
                    padding: '2px 0',
                    minWidth: 0
                }}
            >
                <input
                    ref={!enableSearch && index === 0 ? refInput : undefined}
                    type="checkbox"
                    checked={selectedOptions.includes(option)}
                    onChange={() => toggleStatus(option)}
                    style={{ marginRight: '6px', flexShrink: 0 }}
                />
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                    title={option}
                >
                    {option}
                </span>
            </label>
        </div>
    );
};

const DropdownFilter = ({ filterParam, options, model, onModelChange, enableSearch = false }: CustomFilterDisplayProps & { filterParam: string, options: string[], enableSearch?: boolean }) => {
    const [selectedOptions, setSelectedOptions] = useState<string[]>(model ? model : []);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const refInput = useRef<HTMLInputElement>(null);
    const listRef = useRef<any>(null);

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
        if (!enableSearch || !searchTerm.trim()) {
            return options;
        }

        return options.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm, enableSearch]);

    const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
        if (!params || !params.suppressFocus) {
            refInput.current?.focus();
        }
        // Reset scroll position when dropdown opens
        if (listRef.current) {
            listRef.current.scrollToItem(0);
        }
    }, []);

    useGridFilterDisplay({ afterGuiAttached });

    useEffect(() => {
        if (!model) {
            setSelectedOptions([]);
        }
    }, [model, options]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const savedParam = params.get(filterParam);

        if (savedParam) {
            const parsed = savedParam.split(',');
            setSelectedOptions(parsed);
            onModelChange(parsed);
        }
    }, [options, onModelChange, filterParam]);

    const toggleStatus = (option: string) => {
        const updatedOptions = selectedOptions.includes(option)
            ? selectedOptions.filter((s) => s !== option)
            : [...selectedOptions, option];

        setSelectedOptions(updatedOptions);
        onModelChange(updatedOptions.length === 0 ? null : updatedOptions);
    };

    return (
        <Box sx={{ p: 0.5, minWidth: enableSearch ? 200 : 165 }}>
            {enableSearch && (
                <TextField
                    ref={refInput}
                    size="small"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        mb: 1,
                        width: '100%',
                        '& .MuiInputBase-input': {
                            fontSize: '12px',
                            padding: '6px 8px'
                        }
                    }}
                />
            )}
            <Box sx={{ maxHeight: enableSearch ? 200 : 'none', overflowY: 'hidden' }}>
                {filteredOptions.length > 0 ? (
                    <FixedSizeList
                        ref={listRef}
                        height={Math.min(filteredOptions.length * 25, 8 * 25)}
                        width="100%"
                        itemCount={filteredOptions.length}
                        itemSize={25}
                        itemData={{
                            options: filteredOptions,
                            selectedOptions,
                            toggleStatus,
                            enableSearch,
                            refInput
                        }}
                    >
                        {Row}
                    </FixedSizeList>
                ) : enableSearch ? (
                    <Box sx={{ p: 1, fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
                        No options found
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};

DropdownFilter.doesFilterPass = ({ model, node, handlerParams, blankOption, nullValueFallback }: DoesFilterPassParams<any, any, string[]> & { blankOption?: string, nullValueFallback?: string }) => {
    // If no model or empty model, show all rows (filter is inactive)
    if (!model || model.length === 0) return true;

    const cellValue = handlerParams.getValue(node);

    // Handle blank option if specified
    if (blankOption && model.includes(blankOption)) {
        if (!cellValue || cellValue.trim() === '' || cellValue.trim() === nullValueFallback) {
            return true;
        }
    }

    // Handle regular matching
    if (cellValue && cellValue.trim()) {
        const normalizedCellValue = cellValue.toString().toLowerCase();
        return model.some((selected: string) => {
            // Skip blank option in regular matching
            if (blankOption && selected === blankOption) {
                return false;
            }
            return normalizedCellValue.includes(selected.toLowerCase());
        });
    }

    return false;
};

DropdownFilter.refresh = ({ model }: { model: string[] | null }, filterParam: string) => {
    const params = new URLSearchParams(window.location.search);

    if (!model || model.length === 0) {
        params.delete(filterParam);
    } else {
        params.set(filterParam, model.join(','));
    }

    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? '?' + search : ''}`);
}

export { DropdownFilter };